import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, useCallback } from 'react';
import {
  CreditCard, Zap, Clock, Brain, TrendingDown, BarChart2,
  RefreshCw, ArrowDown, ArrowUp, Minus, ChevronLeft, ChevronRight,
  FileText, X, CheckCircle, AlertCircle, Clock3, Ban,
} from 'lucide-react';
import {
  usageApi, invoiceApi,
  type UsageBalance, type LlmUsageSummary, type ComputeUsageSummary,
  type DailyLedgerRow, type LedgerEntry, type ModelBreakdownRow,
  type UsagePeriod, type Invoice, type MemorySummary, type MemoryStorage,
} from '@/lib/api';
import styles from './_auth.billing.module.css';

export const Route = createFileRoute('/_auth/billing')({
  component: BillingPage,
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

const CREDIT_TO_USD = 0.002;

function fmt(n: number | null | undefined, d = 2) {
  return n == null ? '—' : n.toFixed(d);
}
function fmtC(n: number | null | undefined) {
  if (n == null) return '—';
  const abs = Math.abs(n);
  return abs >= 1000 ? `${(abs / 1000).toFixed(1)}K` : abs.toFixed(2);
}
function fmtUsd(n: number | null | undefined) {
  return n == null ? '—' : `$${n.toFixed(4)}`;
}
function fmtDur(sec: number) {
  return sec < 60 ? `${sec.toFixed(1)}s` : `${(sec / 60).toFixed(1)}m`;
}
function isoToday()  { return new Date().toISOString().slice(0, 10); }
function iso30dAgo() { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); }

// ─── Simple bar chart ────────────────────────────────────────────────────────

function DailyBarChart({ rows }: { rows: DailyLedgerRow[] }) {
  if (!rows.length) return <div className={styles.empty}>No data for this date range</div>;
  const maxU = Math.max(...rows.map(r => r.creditsUsed), 1);
  const W = 700, H = 160;
  const STEP = W / rows.length;
  const BW = Math.max(6, Math.min(28, STEP - 4));

  return (
    <div className={styles.chart}>
      <svg viewBox={`0 0 ${W} ${H + 36}`} className={styles.chartSvg}>
        {[0, 0.5, 1].map(p => (
          <g key={p}>
            <line x1={0} y1={H - p*H} x2={W} y2={H - p*H} stroke="var(--color-border)" strokeWidth={0.5}/>
            <text x={-4} y={H - p*H + 4} textAnchor="end" fontSize={9} fill="var(--color-text-3)">{fmtC(p*maxU)}</text>
          </g>
        ))}
        {rows.map((r, i) => {
          const x = i * STEP + STEP / 2 - BW / 2;
          const uH = (r.creditsUsed / maxU) * H;
          const bH = ((r.creditsBought + r.creditsInvoiced) / maxU) * H;
          return (
            <g key={r.day}>
              <rect x={x} y={H - uH} width={BW * 0.55} height={uH} fill="#f43f5e" opacity={0.8} rx={2}>
                <title>{r.day}: {fmt(r.creditsUsed)} cr used</title>
              </rect>
              {bH > 0 && (
                <rect x={x + BW * 0.57} y={H - bH} width={BW * 0.43} height={bH} fill="#10b981" opacity={0.8} rx={2}>
                  <title>{r.day}: {fmt(r.creditsBought + r.creditsInvoiced)} cr bought/invoiced</title>
                </rect>
              )}
              <text x={i*STEP+STEP/2} y={H+14} textAnchor="middle" fontSize={9} fill="var(--color-text-3)">{r.day.slice(5)}</text>
            </g>
          );
        })}
      </svg>
      <div style={{ display:'flex', gap:18, marginTop:8, fontSize:11, color:'var(--color-text-3)' }}>
        <span style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:10,height:10,background:'#f43f5e',borderRadius:2,display:'inline-block'}}/> Credits used</span>
        <span style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:10,height:10,background:'#10b981',borderRadius:2,display:'inline-block'}}/> Top-up / invoice</span>
      </div>
    </div>
  );
}

// ─── Ledger entry badge ───────────────────────────────────────────────────────

function EntryBadge({ type }: { type: LedgerEntry['entryType'] }) {
  const map: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
    USAGE_DEBIT:   { label:'Usage',   cls:styles.badgeDebit,   icon:<ArrowDown size={9}/> },
    TOPUP_CREDIT:  { label:'Top-up',  cls:styles.badgeCredit,  icon:<ArrowUp size={9}/> },
    INVOICE_DEBIT: { label:'Invoice', cls:styles.badgeInvoice, icon:<ArrowDown size={9}/> },
    ADJUSTMENT:    { label:'Adjust',  cls:styles.badgeAdjust,  icon:<Minus size={9}/> },
  };
  const { label, cls, icon } = map[type] ?? map.ADJUSTMENT;
  return <span className={`${styles.entryBadge} ${cls}`}>{icon} {label}</span>;
}

// ─── Invoice status badge ─────────────────────────────────────────────────────

function InvStatus({ status }: { status: Invoice['status'] }) {
  const map: Record<string, { cls: string; icon: React.ReactNode }> = {
    DRAFT: { cls: styles.invDraft,  icon: <Clock3 size={9}/> },
    OPEN:  { cls: styles.invOpen,   icon: <AlertCircle size={9}/> },
    PAID:  { cls: styles.invPaid,   icon: <CheckCircle size={9}/> },
    VOID:  { cls: styles.invVoid,   icon: <Ban size={9}/> },
  };
  const { cls, icon } = map[status] ?? map.DRAFT;
  return <span className={`${styles.invBadge} ${cls}`}>{icon} {status}</span>;
}

function InvType({ type }: { type: Invoice['invoiceType'] }) {
  if (type === 'PREPAID_TOPUP')
    return <span className={`${styles.invTypeBadge} ${styles.invTypePrepaid}`}>Top-up receipt</span>;
  return <span className={`${styles.invTypeBadge} ${styles.invTypePostpaid}`}>Usage invoice</span>;
}

// ─── Invoice drawer ───────────────────────────────────────────────────────────

function InvoiceDrawer({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) {
  return (
    <div className={styles.drawerOverlay} onClick={onClose}>
      <div className={styles.drawer} onClick={e => e.stopPropagation()}>
        <div className={styles.drawerHeader}>
          <div>
            <div className={styles.drawerTitle}>{invoice.invoiceNumber}</div>
            <div style={{ display:'flex', gap:8, marginTop:6 }}>
              <InvType type={invoice.invoiceType}/>
              <InvStatus status={invoice.status}/>
            </div>
          </div>
          <button id="invoice-drawer-close" className={styles.drawerClose} onClick={onClose}><X size={15}/></button>
        </div>

        <div className={styles.drawerMeta}>
          <div className={styles.drawerMetaRow}>
            <span className={styles.drawerMetaLabel}>Invoice date</span>
            <span className={styles.drawerMetaValue}>{new Date(invoice.createdAt).toLocaleDateString()}</span>
          </div>
          {invoice.periodStart && invoice.periodEnd && (
            <div className={styles.drawerMetaRow}>
              <span className={styles.drawerMetaLabel}>Billing period</span>
              <span className={styles.drawerMetaValue}>
                {new Date(invoice.periodStart).toLocaleDateString()} – {new Date(invoice.periodEnd).toLocaleDateString()}
              </span>
            </div>
          )}
          {invoice.dueAt && (
            <div className={styles.drawerMetaRow}>
              <span className={styles.drawerMetaLabel}>Due date</span>
              <span className={styles.drawerMetaValue}>{new Date(invoice.dueAt).toLocaleDateString()}</span>
            </div>
          )}
          {invoice.paidAt && (
            <div className={styles.drawerMetaRow}>
              <span className={styles.drawerMetaLabel}>Paid on</span>
              <span className={`${styles.drawerMetaValue} ${styles.credit}`}>{new Date(invoice.paidAt).toLocaleDateString()}</span>
            </div>
          )}
          {invoice.stripeInvoiceId && (
            <div className={styles.drawerMetaRow}>
              <span className={styles.drawerMetaLabel}>Stripe ID</span>
              <span className={`${styles.drawerMetaValue} ${styles.tdMono}`} style={{fontSize:'0.75rem'}}>{invoice.stripeInvoiceId}</span>
            </div>
          )}
        </div>

        {/* Line items */}
        <div>
          <div className={styles.sectionTitle} style={{marginBottom:12}}>Line items</div>
          <div className={styles.drawerLineItems}>
            {invoice.lineItems.map((li, i) => (
              <div key={i} className={styles.lineItem}>
                <div className={styles.lineItemDesc}>{li.description}</div>
                <div className={styles.lineItemCredits}>{fmtC(li.credits)} cr</div>
                <div className={styles.lineItemUsd}>${li.amountUsd.toFixed(4)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.drawerTotal}>
          <span className={styles.drawerTotalLabel}>Total</span>
          <div style={{textAlign:'right'}}>
            <div className={styles.drawerTotalValue}>${invoice.totalAmountUsd.toFixed(4)}</div>
            <div style={{fontSize:11,color:'var(--color-text-3)',marginTop:2}}>{fmtC(invoice.totalCredits)} credits</div>
          </div>
        </div>

        <div style={{fontSize:11,color:'var(--color-text-3)',textAlign:'center'}}>
          1 credit = ${CREDIT_TO_USD} USD · Pricing includes 30% platform margin
        </div>
      </div>
    </div>
  );
}

// ─── Invoices tab ────────────────────────────────────────────────────────────

function InvoicesTab({ balance }: { balance: UsageBalance | null }) {
  const [invoices, setInvoices]     = useState<Invoice[]>([]);
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState<Invoice | null>(null);
  const [page, setPage]             = useState(0);
  const [statusFilter, setStatus]   = useState('');
  const [typeFilter, setType]       = useState('');
  const PAGE = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await invoiceApi.list({
        status:      statusFilter || undefined,
        invoiceType: typeFilter   || undefined,
        limit:       PAGE,
        offset:      page * PAGE,
      });
      setInvoices(data);
    } finally { setLoading(false); }
  }, [statusFilter, typeFilter, page]);

  useEffect(() => { void load(); }, [load]);

  const totalUsd = invoices.reduce((s, i) => s + i.totalAmountUsd, 0);

  return (
    <div style={{display:'flex',flexDirection:'column',gap:24}}>
      {/* Summary cards */}
      <div className={styles.statsGrid} style={{gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))'}}>
        <div className={styles.statCard}>
          <div className={styles.statHeader}><FileText size={16} className={styles.iconPrimary}/><span className={styles.statLabel}>Total invoiced</span></div>
          <div className={styles.statValue} style={{fontSize:'1.5rem'}}>${totalUsd.toFixed(2)}</div>
          <div className={styles.statSub}>{invoices.length} invoice{invoices.length !== 1 ? 's' : ''} this page</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statHeader}><AlertCircle size={16} className={styles.iconAmber}/><span className={styles.statLabel}>Outstanding</span></div>
          <div className={styles.statValue} style={{fontSize:'1.5rem'}}>
            ${invoices.filter(i => i.status === 'OPEN').reduce((s,i) => s+i.totalAmountUsd, 0).toFixed(2)}
          </div>
          <div className={styles.statSub}>{invoices.filter(i=>i.status==='OPEN').length} open</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statHeader}><CheckCircle size={16} className={styles.iconEmerald}/><span className={styles.statLabel}>Paid</span></div>
          <div className={styles.statValue} style={{fontSize:'1.5rem'}}>
            ${invoices.filter(i => i.status === 'PAID').reduce((s,i) => s+i.totalAmountUsd, 0).toFixed(2)}
          </div>
          <div className={styles.statSub}>{invoices.filter(i=>i.status==='PAID').length} paid</div>
        </div>
        {balance?.billingMode === 'POSTPAID' && (
          <div className={styles.statCard}>
            <div className={styles.statHeader}><Clock size={16} className={styles.iconCyan}/><span className={styles.statLabel}>Accruing now</span></div>
            <div className={styles.statValue} style={{fontSize:'1.5rem'}}>
              ${((balance.usageAccruedCredits ?? 0) * CREDIT_TO_USD).toFixed(4)}
            </div>
            <div className={styles.statSub}>Will appear on next invoice (cycle day {balance.billingCycleAnchorDay})</div>
          </div>
        )}
      </div>

      {/* Filters + table */}
      <div className={styles.tableCard}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>
            {balance?.billingMode === 'POSTPAID' ? 'Usage invoices (what you owe)' : 'Top-up receipts (what was charged)'}
          </div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            <select
              id="invoice-status-filter"
              className={styles.dateInput}
              value={statusFilter}
              onChange={e => { setStatus(e.target.value); setPage(0); }}
            >
              <option value="">All statuses</option>
              <option value="OPEN">Open</option>
              <option value="PAID">Paid</option>
              <option value="DRAFT">Draft</option>
              <option value="VOID">Void</option>
            </select>
            <select
              id="invoice-type-filter"
              className={styles.dateInput}
              value={typeFilter}
              onChange={e => { setType(e.target.value); setPage(0); }}
            >
              <option value="">All types</option>
              <option value="POSTPAID_USAGE">Usage invoice</option>
              <option value="PREPAID_TOPUP">Top-up receipt</option>
            </select>
          </div>
        </div>

        <div className={styles.tableWrap}>
          {loading ? (
            <div className={styles.skeleton} style={{height:200}}/>
          ) : !invoices.length ? (
            <div className={styles.empty}>
              {balance?.billingMode === 'POSTPAID'
                ? 'No invoices yet — your first invoice will appear at the end of your billing cycle.'
                : 'No receipts yet — receipts appear here after each credit top-up.'}
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Period / Date</th>
                  <th>Credits</th>
                  <th>Amount (USD)</th>
                  <th>Due / Paid</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => (
                  <tr
                    key={inv.id}
                    style={{cursor:'pointer'}}
                    onClick={() => setSelected(inv)}
                  >
                    <td className={styles.tdBold}>{inv.invoiceNumber}</td>
                    <td><InvType type={inv.invoiceType}/></td>
                    <td><InvStatus status={inv.status}/></td>
                    <td className={styles.neutral} style={{fontSize:'0.8125rem'}}>
                      {inv.periodStart && inv.periodEnd
                        ? `${new Date(inv.periodStart).toLocaleDateString()} – ${new Date(inv.periodEnd).toLocaleDateString()}`
                        : new Date(inv.createdAt).toLocaleDateString()}
                    </td>
                    <td className={styles.neutral}>{fmtC(inv.totalCredits)} cr</td>
                    <td className={styles.tdBold}>${inv.totalAmountUsd.toFixed(4)}</td>
                    <td className={styles.neutral} style={{fontSize:'0.8125rem'}}>
                      {inv.paidAt
                        ? <span className={styles.credit}>Paid {new Date(inv.paidAt).toLocaleDateString()}</span>
                        : inv.dueAt ? `Due ${new Date(inv.dueAt).toLocaleDateString()}` : '—'}
                    </td>
                    <td>
                      <button
                        id={`view-invoice-${inv.id}`}
                        className={styles.pageBtn}
                        onClick={e => { e.stopPropagation(); setSelected(inv); }}
                        style={{padding:'4px 10px',fontSize:'0.75rem'}}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className={styles.pagination}>
          <button id="inv-prev" className={styles.pageBtn} disabled={page===0||loading} onClick={() => setPage(p=>p-1)}><ChevronLeft size={14}/></button>
          <span className={styles.pageInfo}>Page {page+1}</span>
          <button id="inv-next" className={styles.pageBtn} disabled={invoices.length<PAGE||loading} onClick={() => setPage(p=>p+1)}><ChevronRight size={14}/></button>
        </div>
      </div>

      {selected && <InvoiceDrawer invoice={selected} onClose={() => setSelected(null)}/>}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

type Tab = 'overview' | 'invoices';

export default function BillingPage() {
  const [tab, setTab]                        = useState<Tab>('overview');
  const [period, setPeriod]                  = useState<UsagePeriod>('today');
  const [balance, setBalance]                = useState<UsageBalance | null>(null);
  const [llmSummary, setLlmSummary]          = useState<LlmUsageSummary | null>(null);
  const [computeSummary, setComputeSummary]  = useState<ComputeUsageSummary | null>(null);
  const [memorySummary, setMemorySummary]    = useState<MemorySummary | null>(null);
  const [memoryStorage, setMemoryStorage]    = useState<MemoryStorage | null>(null);
  const [dailyRows, setDailyRows]            = useState<DailyLedgerRow[]>([]);
  const [entries, setEntries]                = useState<LedgerEntry[]>([]);
  const [modelBreakdown, setModelBreakdown]  = useState<ModelBreakdownRow[]>([]);
  const [loading, setLoading]                = useState(true);
  const [ledgerPage, setLedgerPage]          = useState(0);
  const [fromDate, setFromDate]              = useState(iso30dAgo());
  const [toDate, setToDate]                  = useState(isoToday());
  const PAGE_SIZE = 20;

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const [bal, llm, compute, memory, memStore, daily, ents, models] = await Promise.allSettled([
        usageApi.getBalance(),
        usageApi.getLlmSummary(period),
        usageApi.getComputeSummary(period),
        usageApi.getMemorySummary(period),
        usageApi.getMemoryStorage(),
        usageApi.getDailyReport(fromDate, toDate),
        usageApi.getRecentEntries(PAGE_SIZE, ledgerPage * PAGE_SIZE),
        usageApi.getModelBreakdown(period),
      ]);
      if (bal.status      === 'fulfilled') setBalance(bal.value);
      if (llm.status      === 'fulfilled') setLlmSummary(llm.value);
      if (compute.status  === 'fulfilled') setComputeSummary(compute.value);
      if (memory.status   === 'fulfilled') setMemorySummary(memory.value);
      if (memStore.status === 'fulfilled') setMemoryStorage(memStore.value);
      if (daily.status    === 'fulfilled') setDailyRows(daily.value);
      if (ents.status     === 'fulfilled') setEntries(ents.value);
      if (models.status   === 'fulfilled') setModelBreakdown(models.value);
    } finally { setLoading(false); }
  }, [period, fromDate, toDate, ledgerPage]);

  // Load balance on every tab change (so banner stays fresh on Invoices tab too)
  useEffect(() => {
    void usageApi.getBalance().then(b => setBalance(b)).catch(() => {});
  }, [tab]);

  useEffect(() => {
    if (tab === 'overview') void loadOverview();
  }, [tab, loadOverview]);

  const PERIODS: { label: string; value: UsagePeriod }[] = [
    { label: 'Last hour', value: 'hour' },
    { label: 'Today',     value: 'today' },
    { label: 'This week', value: 'week' },
    { label: '30 days',   value: '30d' },
  ];

  const statusCls = balance?.billingStatus === 'BLOCKED'     ? styles.statusBlocked
                  : balance?.billingStatus === 'LOW_CREDITS'  ? styles.statusLow
                  : styles.statusActive;

  return (
    <div className={styles.page}>
      {/* ── Header ────────────────────────────────── */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}><CreditCard size={28} className={styles.titleIcon}/> Usage &amp; Billing</h1>
          <p className={styles.subtitle}>Credit consumption, cost breakdown, and invoices</p>
        </div>
        {tab === 'overview' && (
          <div className={styles.headerActions}>
            {PERIODS.map(p => (
              <button key={p.value} id={`period-${p.value}`}
                className={`${styles.periodBtn} ${period===p.value ? styles.periodBtnActive : ''}`}
                onClick={() => setPeriod(p.value)}>
                {p.label}
              </button>
            ))}
            <button id="billing-refresh" className={styles.periodBtn} onClick={loadOverview} disabled={loading} title="Refresh">
              <RefreshCw size={14} style={loading ? {animation:'spin 1s linear infinite'} : undefined}/>
            </button>
          </div>
        )}
      </div>

      {/* ── Balance Banner ─────────────────────────── */}
      {balance ? (
        <div className={styles.balanceBanner}>
          <div className={styles.balanceMain}>
            <span className={styles.balanceLabel}>{balance.billingMode === 'PREPAID' ? 'Credit Balance' : 'Accrued Usage'}</span>
            <span className={styles.balanceValue}>
              {balance.billingMode === 'PREPAID' ? fmtC(balance.creditBalance) : fmtC(balance.usageAccruedCredits)}
            </span>
            <span className={styles.balanceSub}>
              {balance.billingMode === 'PREPAID'
                ? `≈ $${(balance.creditBalance * CREDIT_TO_USD).toFixed(2)} remaining`
                : `≈ $${(balance.usageAccruedCredits * CREDIT_TO_USD).toFixed(2)} this cycle (resets day ${balance.billingCycleAnchorDay})`}
            </span>
          </div>
          <div className={styles.balanceDivider}/>
          <div className={styles.balanceStat}>
            <span className={styles.balanceStatLabel}>Mode</span>
            <span className={`${styles.modeBadge} ${balance.billingMode==='PREPAID' ? styles.modePrepaid : styles.modePostpaid}`}>{balance.billingMode}</span>
          </div>
          <div className={styles.balanceDivider}/>
          <div className={styles.balanceStat}>
            <span className={styles.balanceStatLabel}>Status</span>
            <span className={`${styles.statusBadge} ${statusCls}`}>{balance.billingStatus.replace('_',' ')}</span>
          </div>
          {balance.lastInvoicedAt && (
            <><div className={styles.balanceDivider}/>
            <div className={styles.balanceStat}>
              <span className={styles.balanceStatLabel}>Last invoiced</span>
              <span className={styles.balanceStatValue}>{new Date(balance.lastInvoicedAt).toLocaleDateString()}</span>
            </div></>
          )}
        </div>
      ) : (
        <div className={styles.skeleton} style={{height:120}}/>
      )}

      {/* ── Tabs ───────────────────────────────────── */}
      <div className={styles.tabs}>
        <button id="tab-overview"  className={`${styles.tab} ${tab==='overview'  ? styles.tabActive : ''}`} onClick={() => setTab('overview')}>
          <BarChart2 size={15}/> Overview
        </button>
        <button id="tab-invoices"  className={`${styles.tab} ${tab==='invoices'  ? styles.tabActive : ''}`} onClick={() => setTab('invoices')}>
          <FileText size={15}/> Invoices
        </button>
      </div>

      {/* ── Overview tab ───────────────────────────── */}
      {tab === 'overview' && (
        <>
          {/* Stat cards */}
          <div className={styles.statsGrid}>
            {[
              { label:'LLM Calls',   icon:<Zap size={16} className={styles.iconPrimary}/>,
                value: llmSummary ? llmSummary.totalCalls.toLocaleString() : null,
                sub: llmSummary ? `${llmSummary.totalTokens.toLocaleString()} tokens · avg ${fmt(llmSummary.avgDurationMs)}ms` : '' },
              { label:'LLM Credits', icon:<CreditCard size={16} className={styles.iconCyan}/>,
                value: llmSummary ? fmtC(llmSummary.totalCreditCost) : null,
                sub: llmSummary ? `≈ ${fmtUsd(llmSummary.totalProviderCostUsd)} provider cost` : '' },
              { label:'Compute Credits', icon:<Clock size={16} className={styles.iconAmber}/>,
                value: computeSummary ? fmtC(computeSummary.totalCreditCost) : null,
                sub: computeSummary ? `${computeSummary.totalWorkflows} workflows · ${fmtDur(computeSummary.totalActiveSec)} active` : '' },
              { label:'Top Model', icon:<Brain size={16} className={styles.iconEmerald}/>,
                value: llmSummary?.topModel ? llmSummary.topModel.modelName : (llmSummary ? '—' : null),
                valueStyle: {fontSize:'1.1rem',lineHeight:1.3},
                sub: llmSummary?.topModel ? `${llmSummary.topModel.provider} · ${llmSummary.topModel.calls} calls` : '' },
              { label:'Total Spent', icon:<TrendingDown size={16} className={styles.iconRose}/>,
                value: llmSummary && computeSummary ? fmtC((llmSummary.totalCreditCost??0)+(computeSummary.totalCreditCost??0)) : null,
                sub: llmSummary && computeSummary
                  ? `≈ $${(((llmSummary.totalCreditCost??0)+(computeSummary.totalCreditCost??0))*CREDIT_TO_USD).toFixed(4)}`
                  : '' },
              { label:'Token split', icon:<BarChart2 size={16} className={styles.iconCyan}/>,
                value: llmSummary ? `${llmSummary.totalInputTokens.toLocaleString()} ↑` : null,
                sub: llmSummary ? `${llmSummary.totalOutputTokens.toLocaleString()} ↓ output` : '' },
            ].map(c => (
              <div key={c.label} className={styles.statCard}>
                <div className={styles.statHeader}>{c.icon}<span className={styles.statLabel}>{c.label}</span></div>
                {c.value != null
                  ? <><div className={styles.statValue} style={(c as any).valueStyle}>{c.value}</div><div className={styles.statSub}>{c.sub}</div></>
                  : <div className={styles.skeleton} style={{height:40}}/>}
              </div>
            ))}
          </div>

          {/* ── Memory section ─────────────────────────────────── */}
          <div className={styles.chartCard}>
            <div className={styles.sectionHeader}>
              <div>
                <div className={styles.sectionTitle} style={{display:'flex',alignItems:'center',gap:8}}>
                  <Brain size={17} className={styles.iconEmerald}/>
                  Memory costs
                </div>
                <div className={styles.sectionSub}>Short-term (working memory while live) · Long-term (stored at rest) · Write operations</div>
              </div>
            </div>

            {/* 4 memory stat cards */}
            <div className={styles.statsGrid} style={{gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',marginBottom:24}}>
              {/* Short-term */}
              <div className={styles.statCard}>
                <div className={styles.statHeader}><Clock size={15} className={styles.iconCyan}/><span className={styles.statLabel}>Short-term memory</span></div>
                {memorySummary != null ? (
                  <>
                    <div className={styles.statValue} style={{fontSize:'1.4rem'}}>{fmtC(memorySummary.shortTermCredits ?? 0)}</div>
                    <div className={styles.statSub}>Working memory · {memorySummary.shortTermEvents} session{memorySummary.shortTermEvents !== 1 ? 's' : ''}</div>
                    <div className={styles.statSub} style={{fontSize:10}}>0.05 cr/MB-sec · billed per session close</div>
                  </>
                ) : <div className={styles.skeleton} style={{height:50}}/>}
              </div>

              {/* Long-term */}
              <div className={styles.statCard}>
                <div className={styles.statHeader}><Brain size={15} className={styles.iconEmerald}/><span className={styles.statLabel}>Long-term storage</span></div>
                {memorySummary != null ? (
                  <>
                    <div className={styles.statValue} style={{fontSize:'1.4rem'}}>{fmtC(memorySummary.longTermCredits ?? 0)}</div>
                    <div className={styles.statSub}>Stored memories · {memorySummary.longTermEvents} hourly tick{memorySummary.longTermEvents !== 1 ? 's' : ''}</div>
                    <div className={styles.statSub} style={{fontSize:10}}>0.10 cr/MB/day · billed every hour</div>
                  </>
                ) : <div className={styles.skeleton} style={{height:50}}/>}
              </div>

              {/* Write ops */}
              <div className={styles.statCard}>
                <div className={styles.statHeader}><Zap size={15} className={styles.iconAmber}/><span className={styles.statLabel}>Write operations</span></div>
                {memorySummary != null ? (
                  <>
                    <div className={styles.statValue} style={{fontSize:'1.4rem'}}>{fmtC(memorySummary.memoryWriteCredits ?? 0)}</div>
                    <div className={styles.statSub}>{memorySummary.memoryWriteEvents} write{memorySummary.memoryWriteEvents !== 1 ? 's' : ''} to long-term memory</div>
                    <div className={styles.statSub} style={{fontSize:10}}>2 cr/write flat fee</div>
                  </>
                ) : <div className={styles.skeleton} style={{height:50}}/>}
              </div>

              {/* Live storage gauge */}
              <div className={styles.statCard}>
                <div className={styles.statHeader}><TrendingDown size={15} className={styles.iconRose}/><span className={styles.statLabel}>Storage right now</span></div>
                {memoryStorage != null ? (
                  <>
                    <div className={styles.statValue} style={{fontSize:'1.4rem'}}>{memoryStorage.storageMb.toFixed(2)} MB</div>
                    <div className={styles.statSub}>{fmtC(memoryStorage.dailyCredits)} cr/day · ${memoryStorage.dailyUsd.toFixed(4)}/day</div>
                    <div className={styles.statSub} style={{fontSize:10}}>{fmtC(memoryStorage.hourlyCredits)} cr/hour at current size</div>
                  </>
                ) : <div className={styles.skeleton} style={{height:50}}/>}
              </div>
            </div>

            {/* Breakdown table */}
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Memory type</th>
                    <th>How it's charged</th>
                    <th>Rate</th>
                    <th>This period (credits)</th>
                    <th>≈ USD</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><div className={styles.tdBold}>Short-term (working)</div>
                        <div style={{fontSize:11,color:'var(--color-text-3)'}}>In-context memory while workflow/chat is live</div></td>
                    <td className={styles.neutral}>Per session close · MB-seconds × rate</td>
                    <td className={styles.tdMono}>0.05 cr / MB-sec</td>
                    <td className={memorySummary?.shortTermCredits ? styles.debit : styles.neutral}>{memorySummary ? fmtC(memorySummary.shortTermCredits ?? 0) : '—'}</td>
                    <td className={styles.neutral}>{memorySummary ? `$${((memorySummary.shortTermCredits ?? 0) * CREDIT_TO_USD).toFixed(4)}` : '—'}</td>
                  </tr>
                  <tr>
                    <td><div className={styles.tdBold}>Long-term (storage at rest)</div>
                        <div style={{fontSize:11,color:'var(--color-text-3)'}}>Persistent memories in user_memories table</div></td>
                    <td className={styles.neutral}>Hourly cron · MB stored × rate ÷ 24</td>
                    <td className={styles.tdMono}>0.10 cr / MB / day</td>
                    <td className={memorySummary?.longTermCredits ? styles.debit : styles.neutral}>{memorySummary ? fmtC(memorySummary.longTermCredits ?? 0) : '—'}</td>
                    <td className={styles.neutral}>{memorySummary ? `$${((memorySummary.longTermCredits ?? 0) * CREDIT_TO_USD).toFixed(4)}` : '—'}</td>
                  </tr>
                  <tr>
                    <td><div className={styles.tdBold}>Write operations</div>
                        <div style={{fontSize:11,color:'var(--color-text-3)'}}>Each memory.write() call (extraction + storage)</div></td>
                    <td className={styles.neutral}>Per write event · flat fee</td>
                    <td className={styles.tdMono}>2 cr / write</td>
                    <td className={memorySummary?.memoryWriteCredits ? styles.debit : styles.neutral}>{memorySummary ? fmtC(memorySummary.memoryWriteCredits ?? 0) : '—'}</td>
                    <td className={styles.neutral}>{memorySummary ? `$${((memorySummary.memoryWriteCredits ?? 0) * CREDIT_TO_USD).toFixed(4)}` : '—'}</td>
                  </tr>
                  <tr style={{background:'var(--color-bg)'}}>
                    <td colSpan={3} style={{textAlign:'right',fontWeight:700,color:'var(--color-text)'}}>Total memory costs this period</td>
                    <td className={styles.debit} style={{fontWeight:700}}>{memorySummary ? fmtC(memorySummary.totalMemoryCredits ?? 0) : '—'}</td>
                    <td style={{fontWeight:700,color:'var(--color-text)'}}>{memorySummary ? `$${((memorySummary.totalMemoryCredits ?? 0) * CREDIT_TO_USD).toFixed(4)}` : '—'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Daily chart */}

          <div className={styles.chartCard}>
            <div className={styles.sectionHeader}>
              <div><div className={styles.sectionTitle}>Daily credit usage</div>
                   <div className={styles.sectionSub}>Used (red) vs top-up/invoiced (green) per day</div></div>
              <div className={styles.dateRow}>
                <span className={styles.dateLabel}>From</span>
                <input id="from-date" type="date" value={fromDate} max={toDate} className={styles.dateInput} onChange={e=>setFromDate(e.target.value)}/>
                <span className={styles.dateLabel}>to</span>
                <input id="to-date" type="date" value={toDate} min={fromDate} max={isoToday()} className={styles.dateInput} onChange={e=>setToDate(e.target.value)}/>
              </div>
            </div>
            {loading ? <div className={styles.skeleton} style={{height:200}}/> : <DailyBarChart rows={dailyRows}/>}

            {dailyRows.length > 0 && !loading && (
              <div className={styles.tableWrap} style={{marginTop:24}}>
                <table className={styles.table}>
                  <thead><tr><th>Day</th><th>Used</th><th>Top-up/Invoice</th><th>Adjusted</th><th>Net</th><th>Transactions</th></tr></thead>
                  <tbody>
                    {dailyRows.map(r => (
                      <tr key={r.day}>
                        <td className={styles.tdBold}>{r.day}</td>
                        <td className={styles.debit}>{fmtC(r.creditsUsed)}</td>
                        <td className={styles.credit}>{r.creditsBought>0 ? `+${fmtC(r.creditsBought)}` : r.creditsInvoiced>0 ? `-${fmtC(r.creditsInvoiced)}` : '—'}</td>
                        <td className={r.creditsAdjusted!==0?styles.credit:styles.neutral}>{r.creditsAdjusted!==0?fmtC(r.creditsAdjusted):'—'}</td>
                        <td className={r.netChange<0?styles.debit:styles.credit}>{r.netChange>0?'+':''}{fmtC(r.netChange)}</td>
                        <td className={styles.neutral}>{r.entryCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Model breakdown + Transaction log */}
          <div className={styles.twoCol}>
            <div className={styles.tableCard}>
              <div className={styles.sectionHeader}><div className={styles.sectionTitle}>Cost by model</div></div>
              <div className={styles.tableWrap}>
                {loading ? <div className={styles.skeleton} style={{height:160}}/> :
                 !modelBreakdown.length ? <div className={styles.empty}>No LLM calls in this period</div> : (
                  <table className={styles.table}>
                    <thead><tr><th>Model</th><th>Calls</th><th>Tokens</th><th>Credits</th><th>Provider</th></tr></thead>
                    <tbody>
                      {modelBreakdown.map((r,i) => (
                        <tr key={i}>
                          <td><div className={styles.tdBold} style={{fontSize:'0.8125rem'}}>{r.modelName}</div>
                              <div style={{fontSize:10,color:'var(--color-text-3)'}}>{r.provider}</div></td>
                          <td className={styles.tdMono}>{r.calls.toLocaleString()}</td>
                          <td className={styles.tdMono}>{(r.inputTokens+r.outputTokens).toLocaleString()}</td>
                          <td className={styles.debit}>{fmtC(r.creditCost)}</td>
                          <td className={styles.neutral}>{fmtUsd(r.providerCostUsd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className={styles.tableCard}>
              <div className={styles.sectionHeader}><div className={styles.sectionTitle}>Transaction log</div><div className={styles.sectionSub}>Most recent first</div></div>
              <div className={styles.tableWrap}>
                {loading ? <div className={styles.skeleton} style={{height:160}}/> :
                 !entries.length ? <div className={styles.empty}>No transactions yet</div> : (
                  <table className={styles.table}>
                    <thead><tr><th>Type</th><th>Amount</th><th>Description</th><th>Time</th></tr></thead>
                    <tbody>
                      {entries.map(e => (
                        <tr key={e.id}>
                          <td><EntryBadge type={e.entryType}/></td>
                          <td className={e.amount<0?styles.debit:styles.credit}>{e.amount>0?'+':''}{fmtC(e.amount)}</td>
                          <td style={{fontSize:'0.8125rem',maxWidth:200}}>
                            <div className={styles.tdBold} style={{fontSize:'0.8125rem',wordBreak:'break-word'}}>{e.description}</div>
                            {e.sourceType && <div style={{fontSize:10,color:'var(--color-text-3)'}}>{e.sourceType}</div>}
                          </td>
                          <td style={{fontSize:'0.75rem',color:'var(--color-text-3)',whiteSpace:'nowrap'}}>{new Date(e.createdAt).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className={styles.pagination}>
                <button id="ledger-prev" className={styles.pageBtn} disabled={ledgerPage===0||loading} onClick={()=>setLedgerPage(p=>p-1)}><ChevronLeft size={14}/></button>
                <span className={styles.pageInfo}>Page {ledgerPage+1}</span>
                <button id="ledger-next" className={styles.pageBtn} disabled={entries.length<PAGE_SIZE||loading} onClick={()=>setLedgerPage(p=>p+1)}><ChevronRight size={14}/></button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Invoices tab ───────────────────────────── */}
      {tab === 'invoices' && <InvoicesTab balance={balance}/>}
    </div>
  );
}
